import { useCallback, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import type { Tone } from './state.ts';
import { axisClock, frac, ticks } from './timeScale.ts';
import styles from './Ribbon.module.css';

/* ==========================================================================
   Ribbon — discrete state over time.

   THE BAR §5 puts this second in the status-encoding hierarchy, above an
   edge bar and far above a badge: a full-width band with the value printed
   *inside* the segment. It is how a console shows "what was this robot doing,
   and when did it change" without a single row of table.

   A RibbonStack binds several lanes plus a plot to ONE x-scale and one
   crosshair, whose readout is a two-column list of every lane's value at that
   instant. That readout is the difference between a picture and an
   instrument.
   ========================================================================== */

export interface RibbonSegment {
  /** epoch ms, inclusive */
  start: number;
  /** epoch ms, exclusive */
  end: number;
  tone: Tone;
  /** The value itself. Printed inside the segment in mono when it fits. */
  label: string;
  /** Nothing was arriving during this span — drawn hatched, not filled. */
  hollow?: boolean | undefined;
}

/** One line of the crosshair readout contributed by a bound panel. */
export interface ReadoutRow {
  key: string;
  label: string;
  value: string;
  unit?: string | undefined;
  tone?: Tone | undefined;
}

export interface RibbonLane {
  key: string;
  /** Left-gutter name for the series. */
  label: string;
  segments: readonly RibbonSegment[];
}

/** The label needs about this much room before it is worth printing. */
const LABEL_MIN_PX = 46;
/** Below this a segment is a hairline and the lane is over-zoomed. */
const HAIRLINE_PX = 2.5;

function valueAt(lane: RibbonLane, t: number): RibbonSegment | null {
  for (const s of lane.segments) if (t >= s.start && t < s.end) return s;
  return null;
}

function summarise(lane: RibbonLane): string {
  const parts = lane.segments.map((s) => `${s.label} at ${axisClock(s.start)}`);
  return `${lane.label}: ${parts.join(', ') || 'no data'}`;
}

/* --------------------------------------------------------------------------
   One lane.
   -------------------------------------------------------------------------- */

export function Ribbon({
  segments,
  from,
  to,
  label,
  width,
  height,
}: {
  segments: readonly RibbonSegment[];
  from: number;
  to: number;
  /** Accessible name; the left gutter is drawn by RibbonStack, not here. */
  label: string;
  /** Measured px width of the track, used only to decide what text fits. */
  width?: number | undefined;
  height?: number | undefined;
}) {
  const px = width ?? 0;
  const style = height === undefined ? undefined : { height: `${height}px` };
  let overZoomed = false;

  return (
    <div
      className={styles.lane}
      style={style}
      role="img"
      aria-label={summarise({ key: label, label, segments })}
    >
      {segments.map((s) => {
        const a = frac(s.start, from, to);
        const b = frac(s.end, from, to);
        if (b <= a) return null;
        const w = (b - a) * px;
        if (px > 0 && w < HAIRLINE_PX) overZoomed = true;
        return (
          <span
            key={`${s.start}-${s.label}`}
            className={styles.seg}
            data-tone={s.tone}
            data-hollow={s.hollow === true}
            style={{ left: `${a * 100}%`, width: `${(b - a) * 100}%` }}
            title={`${s.label} · ${axisClock(s.start)} → ${axisClock(s.end)}`}
          >
            {(px === 0 || w >= LABEL_MIN_PX) && (
              <span className={styles.segLabel}>{s.label}</span>
            )}
          </span>
        );
      })}
      {overZoomed && <span className={styles.zoomHint}>(zoom for more)</span>}
    </div>
  );
}

/* --------------------------------------------------------------------------
   The ruler. Ticks land on round UTC boundaries and wear a leading pipe.
   -------------------------------------------------------------------------- */

export function TimeAxis({
  from,
  to,
  target = 6,
  now,
}: {
  from: number;
  to: number;
  target?: number | undefined;
  /** Draws the live playhead in the accent, if it is inside the window. */
  now?: number | undefined;
}) {
  const t = ticks(from, to, target);
  const nowF = now !== undefined && now >= from && now <= to ? frac(now, from, to) : null;
  return (
    <div className={styles.axis}>
      {t.map((tk) =>
        tk.f > 0.965 ? null : (
          <span key={tk.t} className={styles.tick} style={{ left: `${tk.f * 100}%` }}>
            {tk.label}
          </span>
        ),
      )}
      {nowF !== null && (
        <span className={styles.axisNow} style={{ left: `${nowF * 100}%` }}>
          now
        </span>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   The stack: lanes + optional bound content + one shared crosshair.
   -------------------------------------------------------------------------- */

export function RibbonStack({
  lanes,
  from,
  to,
  now,
  gutter = 88,
  laneHeight,
  children,
  readoutRows,
  footer,
}: {
  lanes: readonly RibbonLane[];
  from: number;
  to: number;
  /** Live playhead. Omit when the window is history. */
  now?: number | undefined;
  /** Width of the left label column. Every lane aligns to it. */
  gutter?: number | undefined;
  laneHeight?: number | undefined;
  /**
   * Anything else bound to the same x-scale — a Plot, usually. Pass a
   * function to receive the cursor time and hand it to `<Plot cursor=…
   * readout={false} />`, so the whole stack shares ONE crosshair.
   */
  children?: ReactNode | ((cursor: number | null) => ReactNode);
  /**
   * Extra rows folded into the single crosshair readout — the values a bound
   * Plot would otherwise print in a second box that disagrees about the
   * instant. Return them in the order they should read.
   */
  readoutRows?: ((t: number) => readonly ReadoutRow[]) | undefined;
  /** Right-aligned mono facts under the axis (window length, source). */
  footer?: ReactNode;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(0);
  const [cursor, setCursor] = useState<{ f: number; t: number } | null>(null);

  const measure = useCallback((el: HTMLDivElement | null) => {
    trackRef.current = el;
    if (el) setW(el.getBoundingClientRect().width);
  }, []);

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width !== w) setW(r.width);
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    setCursor({ f, t: from + f * (to - from) });
  };

  const extra = cursor === null || readoutRows === undefined ? [] : readoutRows(cursor.t);
  const readout = cursor === null ? null : lanes.map((l) => ({
    key: l.key,
    label: l.label,
    seg: valueAt(l, cursor.t),
  }));

  return (
    <div className={styles.stack} style={{ ['--gutter-w' as string]: `${gutter}px` }}>
      <div
        className={styles.body}
        onPointerMove={onMove}
        onPointerLeave={() => setCursor(null)}
      >
        <div className={styles.gutterCol}>
          {lanes.map((l) => (
            <div key={l.key} className={styles.laneLabel} style={laneHeight === undefined ? undefined : { height: `${laneHeight}px` }}>
              {l.label}
            </div>
          ))}
        </div>
        <div className={styles.trackCol} ref={measure}>
          {lanes.map((l) => (
            <Ribbon
              key={l.key}
              segments={l.segments}
              from={from}
              to={to}
              label={l.label}
              width={w}
              {...(laneHeight === undefined ? {} : { height: laneHeight })}
            />
          ))}
          {typeof children === 'function' ? children(cursor?.t ?? null) : children}
          {now !== undefined && now >= from && now <= to && (
            <span className={styles.playhead} style={{ left: `${frac(now, from, to) * 100}%` }} />
          )}
          {cursor !== null && (
            <span className={styles.crosshair} style={{ left: `${cursor.f * 100}%` }} />
          )}
          {cursor !== null && readout !== null && (
            <div className={styles.readout} data-side={cursor.f > 0.55 ? 'left' : 'right'}>
              <div className={styles.readoutTime}>{axisClock(cursor.t)}</div>
              {readout.map((r) => (
                <div key={r.key} className={styles.readoutRow}>
                  <span className={styles.readoutKey}>{r.label}</span>
                  <span
                    className={styles.readoutVal}
                    data-tone={r.seg?.tone ?? 'neutral'}
                    data-known={r.seg !== null}
                  >
                    {r.seg?.label ?? '—'}
                  </span>
                </div>
              ))}
              {extra.length > 0 && <div className={styles.readoutRule} />}
              {extra.map((r) => (
                <div key={r.key} className={styles.readoutRow}>
                  <span className={styles.readoutKey}>{r.label}</span>
                  <span className={styles.readoutVal} data-tone={r.tone} data-known>
                    {r.value}
                    {r.unit !== undefined && (
                      <span className={styles.readoutUnit}>{r.unit}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.axisRow}>
        <div className={styles.gutterCol} />
        <div className={styles.trackCol}>
          <TimeAxis from={from} to={to} {...(now === undefined ? {} : { now })} />
        </div>
      </div>
      {footer !== undefined && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
